import { cn } from '@/lib/utils';

/** A step as the trail shows it. */
export type TrailStep = { key: string; label: string };

/**
 * Where a wizard is, named step by step.
 *
 * Lives in the header slot of `AppDialogLayout`, under the title. A one-step
 * flow renders no trail at all — the caller decides that, because "how many
 * steps are there" is a question about the flow, not about the chrome.
 */
export function StepTrail({ steps, currentIndex }: { steps: TrailStep[]; currentIndex: number }) {
    return (
        <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {steps.map((step, index) => {
                const isDone = index < currentIndex;
                const isCurrent = index === currentIndex;

                return (
                    <li key={step.key} className="flex items-center gap-2">
                        {index > 0 && <span aria-hidden="true" className="h-px w-4 bg-border" />}
                        <span
                            aria-current={isCurrent ? 'step' : undefined}
                            className={cn(
                                'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                                isCurrent && 'bg-brand-subtle text-brand',
                                isDone && 'text-success',
                                !isCurrent && !isDone && 'text-muted-foreground',
                            )}
                        >
                            <span
                                className={cn(
                                    'flex size-4 shrink-0 items-center justify-center rounded-full text-xs font-semibold leading-none',
                                    isCurrent && 'bg-brand text-primary-foreground',
                                    isDone && 'bg-success/15 text-success',
                                    !isCurrent && !isDone && 'bg-surface-muted',
                                )}
                            >
                                {isDone ? '✓' : index + 1}
                            </span>
                            {step.label}
                        </span>
                    </li>
                );
            })}
        </ol>
    );
}
